import classnames from 'classnames'
import { css } from 'emotion'
import { LifeInsuranceStep, Product } from 'even-ts-static'
import * as React from 'react'
import { connect } from 'react-redux'

import { fetchRouterQuestions, stepSelected } from 'common/actions'
import { getContrastingColor } from 'embed/utils'

import ThemeConsumer from 'embed/components/ThemeConsumer'
import { applicationLifeInsuranceRouterQuestionsGroupsSelector } from 'embed/selectors/application'
import { isLeapLifeBrandedSelector } from 'embed/selectors/config'
import { LifeInsuranceQuestionGroups, MultiProductDispatch } from 'embed/constants'
import { LeapLifeLogo } from '../LeapLifeLogo'

const IconPick = require('./icon-pick.svg')
const IconSearch = require('./icon-search.svg')
const IconTime = require('./icon-time.svg')

import { MultiProductState } from 'embed/reducers'
import AdvertiserDisclosure from '../../AdvertiserDisclosure'

const styles = require('./styles.scss')

interface StoreProps {
  groups: LifeInsuranceQuestionGroups
  isLeapLifeBranded: boolean
}

interface DispatchProps {
  dispatchApplicationStepSelected: () => void
  dispatchFetchRouterQuestions: () => void
}

type Props = StoreProps & DispatchProps

const PreviewStepComponent = ({
  dispatchApplicationStepSelected,
  dispatchFetchRouterQuestions,
  groups,
  isLeapLifeBranded,
}: Props) => {
  React.useEffect(() => {
    if (groups.length === 0) {
      dispatchFetchRouterQuestions()
    }
  }, [])

  return (
    <ThemeConsumer>
      {({
        backdropStyles,
        borderRadiusLarge,
        borderRadiusSmall,
        boxStyles,
        colorBorder,
        ctaStyles,
        colorSecondary,
        colorTextPrimary,
      }) => (
        <div
          className={classnames(
            styles.preview,
            css`
              background-color: ${backdropStyles.backgroundColor};
              border-color: ${colorBorder};
              border-radius: ${borderRadiusLarge};
            `,
          )}
          style={boxStyles}
        >
          <AdvertiserDisclosure className={classnames(styles.advertiserDisclosure)} />
          <h2 className={styles.tagline}>
            Get Matched With Life Insurance
            <br />
            {isLeapLifeBranded ? (
              <>Quotes From Top Providers</>
            ) : (
              <>
                Quotes From Top Providers via <LeapLifeLogo />
              </>
            )}
          </h2>
          <div className={styles.container}>
            <div className={styles.item}>
              <div className={styles.icon}>
                <IconSearch
                  className={css`
                    color: ${colorSecondary};
                  `}
                />
              </div>
              <div className={styles.content}>
                <div className={styles.title}>Browse our marketplace</div>
                <div className={styles.description}>All of our partners are A and A+ rated life insurance carriers</div>
              </div>
            </div>
            <div className={styles.item}>
              <div className={styles.icon}>
                <IconPick
                  className={css`
                    color: ${colorSecondary};
                  `}
                />
              </div>
              <div className={styles.content}>
                <div className={styles.title}>Pick your plan</div>
                <div className={styles.description}>Answer a few basic questions to get matched with a provider</div>
              </div>
            </div>
            <div className={styles.item}>
              <div className={styles.icon}>
                <IconTime
                  className={css`
                    color: ${colorSecondary};
                  `}
                />
              </div>
              <div className={styles.content}>
                <div className={styles.title}>Apply in minutes</div>
                <div className={styles.description}>
                  Most individuals can easily submit applications online or by phone
                </div>
              </div>
            </div>
          </div>
          <div className={styles.cta}>
            <button
              className={classnames(
                styles.button,
                css(`
                border-radius: ${borderRadiusSmall};
                color: ${getContrastingColor(ctaStyles?.backgroundColor, { black: colorTextPrimary })};
              `),
              )}
              style={ctaStyles}
              onClick={dispatchApplicationStepSelected}
            >
              Get Started
            </button>
          </div>
          <div className={styles.message}>No e-mail or phone number required to get a quote.</div>
          <h4 className={classnames(styles.trustedPartnersHeader)}>Our Trusted Carriers</h4>
          <div className={styles.logos}>
            <img
              src="https://images.evenfinancial.com/logos/prod/life-insurance-assets/pacific-life.svg"
              alt="Pacific Life"
              width="140"
            />
            <img
              src="https://images.evenfinancial.com/logos/prod/life-insurance-assets/legal-general.svg"
              alt="Legal & General"
              width="140"
            />
            <img
              src="https://images.evenfinancial.com/logos/prod/life-insurance-assets/protective.svg"
              alt="Protective"
              width="140"
            />
          </div>
          <div className={styles.logos}>
            <img
              src="https://images.evenfinancial.com/logos/prod/life-insurance-assets/assurity.svg"
              alt="Assurity"
              width="140"
            />
            <img
              src="https://images.evenfinancial.com/logos/prod/life-insurance-assets/prudential.svg"
              alt="Prudential"
              width="140"
            />
            <img
              src="https://images.evenfinancial.com/logos/prod/life-insurance-assets/lincoln.svg"
              alt="Lincoln Financial Group"
              width="140"
            />
          </div>
        </div>
      )}
    </ThemeConsumer>
  )
}

const mapStateToProps = (state: MultiProductState): StoreProps => ({
  groups: applicationLifeInsuranceRouterQuestionsGroupsSelector(state),
  isLeapLifeBranded: isLeapLifeBrandedSelector(state),
})

const mapDispatchToProps = (dispatch: MultiProductDispatch) => ({
  dispatchApplicationStepSelected: () => dispatch(stepSelected(Product.LifeInsurance, LifeInsuranceStep.Application)),
  dispatchFetchRouterQuestions: () => dispatch(fetchRouterQuestions()),
})

export const PreviewStep = connect(mapStateToProps, mapDispatchToProps)(PreviewStepComponent)
